/**
 * _directory_cards.js — the /find results list: which features show, in what
 * order, and the card each one draws.
 *
 * Pure functions over GeoJSON features from /find/listings.geojson, so the map
 * and the list always read the same data. Cards are built with DOM calls, not
 * HTML strings: names and suburbs are third-party text, and a listing named
 * "<img onerror=...>" must render as those characters.
 *
 * Imported by directory_map_controller.js.
 */

// The list stops drawing cards past this many. A whole state at low zoom is
// thousands of businesses; nobody scrolls that, and drawing it costs the phone.
export const LIST_LIMIT = 80;

/** Does a feature pass the trade chip and the search box? */
export function featureMatches(feature, trade, query) {
    const p = feature.properties;
    if (trade && p.trade !== trade) return false;
    if (!query) return true;
    const haystack = `${p.name} ${p.area || ''} ${p.trade_label || ''}`.toLowerCase();
    return query.toLowerCase().split(/\s+/).every((word) => haystack.includes(word));
}

/**
 * The features to list: those inside the map's view when there is a map,
 * nearest the centre first. Chippie users come first at equal distance only
 * through the tiny spread, so the order is about distance, not paid placement.
 */
export function visibleFeatures(features, bounds, center) {
    const inView = bounds
        ? features.filter((f) => bounds.contains(f.geometry.coordinates))
        : features.slice();
    if (center) {
        inView.sort((a, b) => distance(a, center) - distance(b, center));
    }
    return inView;
}

function distance(feature, center) {
    const [lng, lat] = feature.geometry.coordinates;
    const dx = (lng - center.lng) * Math.cos((lat * Math.PI) / 180);
    const dy = lat - center.lat;
    return dx * dx + dy * dy;
}

/** "12 tradies in view", or the honest overflow line past LIST_LIMIT. */
export function countLabel(shown, total, hasMap) {
    const noun = (n) => (n === 1 ? 'tradie' : 'tradies');
    if (total === 0) return hasMap ? 'No tradies here yet. Zoom out or try another trade.' : 'No tradies match.';
    const where = hasMap ? ' in view' : '';
    if (total > shown) return `${total.toLocaleString()} ${noun(total)}${where}. Showing the nearest ${shown}.`;
    return `${total.toLocaleString()} ${noun(total)}${where}`;
}

/** One list card for a feature. */
export function buildCard(feature) {
    const p = feature.properties;
    const item = document.createElement('li');
    item.className = 'find-card';
    item.dataset.slug = p.slug;

    const button = el('button', 'find-card-main');
    button.type = 'button';
    button.dataset.action = 'directory-map#pick';
    button.dataset.slug = p.slug;
    button.append(
        el('span', `find-card-dot find-card-dot--${p.kind}`),
        textEl('strong', 'find-card-name', p.name),
        textEl('span', 'find-card-meta', [p.trade_label, p.area].filter(Boolean).join(' · ')),
    );
    if (p.kind === 'chippie') {
        button.append(textEl('span', 'find-card-badge mono', 'On Chippie'));
    }

    const link = textEl('a', 'find-card-link', 'View');
    link.href = p.url;
    link.setAttribute('aria-label', `View ${p.name}`);

    item.append(button, link);
    return item;
}

/** The popup body shown over a selected point. */
export function buildPopup(feature) {
    const p = feature.properties;
    const box = el('div', 'find-pop');
    box.append(
        textEl('strong', 'find-pop-name', p.name),
        textEl('span', 'find-pop-meta', [p.trade_label, p.area].filter(Boolean).join(' · ')),
    );
    if (p.pin_note) {
        box.append(textEl('span', 'find-pop-pin', p.pin_note));
    }
    const link = textEl('a', 'find-pop-link', p.has_phone ? 'Call or enquire' : 'Send an enquiry');
    link.href = p.url;
    box.append(link);
    return box;
}

function el(tag, className) {
    const node = document.createElement(tag);
    node.className = className;
    return node;
}

function textEl(tag, className, text) {
    const node = el(tag, className);
    node.textContent = text || '';
    return node;
}
